/**
 * YouTubeScraper — wraps Apify `streamers/youtube-scraper`.
 * Same page pattern as InstagramScraper/LinkedInScraper: 3 sub-tabs,
 * KPI strip, results grid, auto-refresh polling.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import KanbanTodayBadge, { notifyLeadConverted } from "../components/KanbanTodayBadge";
import { toast } from "sonner";
import {
  Youtube, Search, Loader2, Mail, ExternalLink, UserPlus, Filter,
  Play, Tv, Download, Eye, BookOpen, ShieldCheck,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import ScraperHelp from "../components/ScraperHelp";
import { useLang } from "../i18n/LangContext";
import { useAutoRefresh, AutoRefreshToggle } from "../hooks/useAutoRefresh";
import { useTrialLock } from "../hooks/useTrialLock";
import { blockExportIfTrial } from "../components/TrialLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function YouTubeScraper() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [tab, setTab] = useState("scrape");
  const [stats, setStats] = useState({ total_videos: 0, videos_with_email: 0, unique_channels: 0, top_channels: [] });

  const reloadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/youtube/stats`);
      setStats(data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[YouTubeScraper] stats fetch failed:", e?.message || e);
      }
    }
  }, []);

  useEffect(() => { reloadStats(); }, [reloadStats]);
  const auto = useAutoRefresh(reloadStats, 30000, { defaultEnabled: true });

  return (
    <div className="min-h-screen" style={{ background: "var(--al-bg)" }} data-testid="yt-scraper-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)] flex flex-col">
        <header className="flex items-center gap-3 px-4 lg:px-8 h-[70px] border-b border-[var(--al-border)] bg-[var(--al-card-bg)]">
          <Logo size={28} />
          <div className="w-9 h-9 rounded-lg bg-[#FF0000] text-white flex items-center justify-center">
            <Youtube className="w-5 h-5" />
          </div>
          <h1 className="text-[16px] font-bold tracking-tight">
            <span className="text-[#FF0000]">YouTube</span>
            <span className="text-[var(--al-text-muted)] ml-1.5 font-normal">— Scraper</span>
          </h1>
          <span className="ml-3 text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase">Apify · Real</span>
          <AutoRefreshToggle {...auto} label={isPt ? "Auto-atualizar" : "Auto-refresh"} />
          <div className="ml-auto flex items-center gap-2">
            <LangToggle />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 px-6 lg:px-10 py-6 max-w-[1320px] w-full">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Kpi testId="yt-kpi-total" label={isPt ? "Vídeos coletados" : "Videos captured"} value={stats.total_videos} tone="text-[#FF0000]" />
            <Kpi testId="yt-kpi-with-email" label={isPt ? "Com e-mail" : "With email"} value={stats.videos_with_email} tone="text-emerald-600" />
            <Kpi testId="yt-kpi-channels" label={isPt ? "Canais únicos" : "Unique channels"} value={stats.unique_channels} tone="text-indigo-600" />
            <Kpi testId="yt-kpi-top" label={isPt ? "Top canais" : "Top channels"} value={(stats.top_channels || []).length} tone="text-amber-600" />
          </div>

          <div className="flex gap-1 border-b border-[var(--al-border)] mb-5">
            {[
              { id: "scrape", icon: Search, en: "Scrape", pt: "Coletar" },
              { id: "results", icon: Tv, en: "Results", pt: "Resultados" },
              { id: "runs", icon: Play, en: "Runs", pt: "Execuções" },
              { id: "help", icon: BookOpen, en: "Help", pt: "Ajuda" },
            ].map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} type="button" onClick={() => setTab(t.id)}
                  data-testid={`yt-tab-${t.id}`}
                  className={`px-4 h-10 inline-flex items-center gap-2 text-[13px] font-bold rounded-t-md ${active ? "bg-[var(--al-card-bg)] border-x border-t border-[var(--al-border)] text-[#FF0000] -mb-px" : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"}`}>
                  <Icon className="w-3.5 h-3.5" /> {isPt ? t.pt : t.en}
                </button>
              );
            })}
          </div>

          {tab === "scrape" && <ScrapeTab isPt={isPt} onDone={() => { reloadStats(); setTab("results"); }} />}
          {tab === "results" && <ResultsTab isPt={isPt} stats={stats} onMutate={reloadStats} />}
          {tab === "runs" && <RunsTab isPt={isPt} onIngested={reloadStats} />}
          {tab === "help" && (
            <ScraperHelp
              title={isPt ? "Como usar o YouTube Scraper" : "How to use the YouTube Scraper"}
              intro={isPt
                ? "Busque vídeos por palavra-chave ou URL de canal. O actor extrai título, canal, métricas e e-mails que aparecem na descrição/about — perfeito para outreach a creators."
                : "Search videos by keyword or channel URL. The actor extracts title, channel, metrics and emails found in description/about — ideal for outreach to creators."}
              steps={[
                {
                  title: isPt ? "1. Conecte sua Apify" : "1. Connect Apify",
                  body: isPt ? "APIFY_API_TOKEN em Integrations." : "APIFY_API_TOKEN in Integrations.",
                  link: { href: "/integrations", label: isPt ? "Abrir Integrations" : "Open Integrations" },
                },
                {
                  title: isPt ? "2. Defina buscas ou URLs" : "2. Set queries or URLs",
                  body: isPt
                    ? "Buscas como 'roofing Orlando FL' (separe por vírgula) OU URLs diretas de canais (uma por linha)."
                    : "Queries like 'roofing Orlando FL' (comma-separated) OR direct channel URLs (one per line).",
                },
                {
                  title: isPt ? "3. Limite por busca" : "3. Limit per query",
                  body: isPt
                    ? "Padrão = 10 (sync). Para coletas grandes (200+) prefira background. Mantemos o limite baixo no modo sync para evitar bloqueios e queimar créditos."
                    : "Default = 10 (sync). Use background for big runs (200+). We keep sync low to avoid blocks and burning credits.",
                },
                {
                  title: isPt ? "4. Estabilidade automática" : "4. Built-in stability",
                  body: isPt
                    ? "Toda execução usa Residential Proxies + downloadTimeout 60s + minDelay 2s. Quando há falha em <5s, retornamos o log do Apify diretamente — basta ler a mensagem de erro retornada."
                    : "Every run uses Residential Proxies + 60s downloadTimeout + 2s minDelay. On <5s failures we return the Apify log right in the error so you can read what blocked the run.",
                },
                {
                  title: isPt ? "5. Filtre e exporte" : "5. Filter and export",
                  body: isPt
                    ? "Use os filtros 'Título contém', 'Canal contém' e 'Com email' para isolar prospects. Exporte CSV ou clique em CRM para virar lead."
                    : "Use 'Title contains', 'Channel contains' and 'With email' to isolate prospects. Export CSV or click CRM to convert to lead.",
                },
              ]}
              tips={isPt ? [
                "Crie buscas hyper-locais ('plumber Tampa FL') para conseguir contatos B2B reais.",
                "Vídeos com >5k views e e-mail na bio são leads quentes — quase sempre creators monetizando.",
              ] : [
                "Use hyper-local queries ('plumber Tampa FL') to land real B2B contacts.",
                "Videos with >5k views + email in bio are hot leads — usually creators monetizing.",
              ]}
              troubleshooting={isPt ? [
                { problem: "Sem e-mails", solution: "Nem todo creator expõe e-mail. Combine com a aba Resultados sem filtro 'Email' e use Hunter no CRM." },
                { problem: "Run lento", solution: "Apify tem fila gratuita — assinatura paga reduz tempo. Use background." },
              ] : [
                { problem: "No emails", solution: "Not every creator exposes email. Drop the 'With email' filter and use Hunter in CRM." },
                { problem: "Slow run", solution: "Apify free tier has a queue — paid plans run instantly. Use background." },
              ]}
              links={[
                { href: "https://apify.com/streamers/youtube-scraper", label: "Apify · youtube-scraper" },
              ]}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone, testId }) {
  return (
    <div className="al-card p-4" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-semibold">{label}</div>
      <div className={`text-[26px] font-bold mt-1 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

function ScrapeTab({ isPt, onDone }) {
  const [queries, setQueries] = useState("roofing Orlando FL, HVAC Miami FL");
  const [startUrls, setStartUrls] = useState("");
  const [maxVideos, setMaxVideos] = useState(10);
  const [busy, setBusy] = useState(false);

  // Client-side guard: warn before hitting the backend if any URL is bad —
  // YouTube's residential-proxy run still costs Apify credits even when the
  // URL is malformed. Cheap mitigation.
  const validateUrl = (u) =>
    /^https:\/\/(www\.|m\.)?youtube\.com\//.test(u);

  const submit = async (sync) => {
    const q = queries.split(",").map((s) => s.trim()).filter(Boolean);
    const urls = startUrls.split("\n").map((s) => s.trim()).filter(Boolean);
    if (q.length === 0 && urls.length === 0) {
      toast.error(isPt ? "Adicione pelo menos uma busca ou URL." : "Add at least one query or URL.");
      return;
    }
    const badUrls = urls.filter((u) => !validateUrl(u));
    if (badUrls.length) {
      toast.error(
        isPt
          ? `URL inválida: ${badUrls[0]} — use https://www.youtube.com/...`
          : `Invalid URL: ${badUrls[0]} — must start with https://www.youtube.com/...`
      );
      return;
    }
    setBusy(true);
    try {
      const payload = { queries: q, start_urls: urls, max_videos: Number(maxVideos), sync };
      const { data } = await axios.post(`${API}/youtube/scrape`, payload, { timeout: 180000 });
      if (data.mode === "sync") {
        toast.success(isPt ? `${data.inserted} novos vídeos` : `${data.inserted} new videos`);
        if (onDone) onDone();
      } else {
        toast.success(isPt ? `Run iniciada: ${data.run_id}` : `Run started: ${data.run_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="al-card p-6 space-y-5 max-w-3xl" data-testid="yt-scrape-form">
      {/* Stability banner — applied to every YouTube run via residential
          proxies + 60s downloadTimeout + 2s minDelay, fixing the prior
          "fail in 1-2s" pattern caused by datacenter IP fingerprinting. */}
      <div className="flex items-start gap-2.5 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="yt-stability-info">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="text-[11.5px] leading-snug">
          <strong>{isPt ? "Estabilidade ativada" : "Stability hardened"}:</strong>{" "}
          {isPt
            ? "Residential Proxies + downloadTimeout 60s + minDelay 2s + validação de URL. Falhas instantâneas (<5s) agora retornam o log do Apify para diagnóstico."
            : "Residential Proxies + 60s downloadTimeout + 2s minDelay + URL validation. Sub-5s failures now return the Apify log for diagnosis."}
        </div>
      </div>
      <div>
        <div className="text-[10.5px] uppercase text-[var(--al-text-muted)] font-semibold mb-1.5">
          {isPt ? "Buscas (separadas por vírgula)" : "Queries (comma-separated)"}
        </div>
        <input type="text" value={queries} onChange={(e) => setQueries(e.target.value)}
          placeholder='e.g. "roofing Orlando FL, HVAC Miami FL"'
          data-testid="yt-queries"
          className="w-full al-input h-10 text-[13px]" />
      </div>
      <div>
        <div className="text-[10.5px] uppercase text-[var(--al-text-muted)] font-semibold mb-1.5">
          {isPt ? "URLs diretas (uma por linha) — opcional" : "Direct URLs (one per line) — optional"}
        </div>
        <textarea value={startUrls} onChange={(e) => setStartUrls(e.target.value)} rows={3}
          placeholder="https://youtube.com/@somechannel"
          data-testid="yt-start-urls"
          className="w-full al-input p-3 text-[12.5px] font-mono" />
      </div>
      <div>
        <div className="text-[10.5px] uppercase text-[var(--al-text-muted)] font-semibold mb-1.5">
          {isPt ? "Máx. vídeos por busca" : "Max videos per query"}
        </div>
        <input type="number" min="1" max="200" value={maxVideos}
          onChange={(e) => setMaxVideos(e.target.value)}
          data-testid="yt-max-videos"
          className="w-full al-input h-9 text-[12.5px] tabular-nums max-w-[140px]" />
      </div>
      <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--al-border)]">
        <button type="button" onClick={() => submit(true)} disabled={busy}
          data-testid="yt-scrape-sync"
          className="h-10 px-5 rounded-lg bg-[#FF0000] hover:bg-[#c41d1d] text-white font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {isPt ? "Buscar agora (sync)" : "Scrape now (sync)"}
        </button>
        <button type="button" onClick={() => submit(false)} disabled={busy}
          data-testid="yt-scrape-async"
          className="h-10 px-4 rounded-lg border-2 border-[#FF0000] text-[#FF0000] hover:bg-[#FEE2E2] font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
          <Play className="w-4 h-4" /> {isPt ? "Rodar em background" : "Run in background"}
        </button>
      </div>
    </div>
  );
}

function ResultsTab({ isPt, stats, onMutate }) {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ q: "", channel: "", has_email: null });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = { limit: 60 };
    if (filters.q) params.q = filters.q;
    if (filters.channel) params.channel = filters.channel;
    if (filters.has_email !== null) params.has_email = filters.has_email;
    const { data } = await axios.get(`${API}/youtube/videos`, { params });
    setItems(data.items || []);
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, 30000, { defaultEnabled: true });

  const promote = async (v) => {
    try {
      const { data } = await axios.post(`${API}/youtube/videos/${v.id}/convert-to-lead`);
      notifyLeadConverted();
      toast.success(
        isPt ? "Lead criado e enviado ao Kanban" : "Lead created and sent to Kanban",
        {
          description: isPt
            ? "Visível na coluna NEW do CRM Kanban."
            : "Visible in the NEW column of the CRM Kanban.",
          action: { label: isPt ? "Abrir Kanban" : "Open Kanban",
                    onClick: () => navigate(data.kanban_url || "/kanban") },
          duration: 6000,
        }
      );
      onMutate && onMutate();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const { onTrial } = useTrialLock();
  const exportCsv = () => {
    if (blockExportIfTrial(onTrial, "export CSV")) return;
    if (items.length === 0) return;
    const header = ["title","channel_name","url","views","likes","emails","published_at","created_at"];
    const rows = items.map((p) => header.map((k) =>
      `"${String(Array.isArray(p[k]) ? p[k].join(";") : (p[k] ?? "")).replace(/"/g, '""').replace(/\n/g, " ")}"`).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `youtube_videos_${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="grid lg:grid-cols-4 gap-5" data-testid="yt-results">
      <aside className="al-card p-5 space-y-5 lg:col-span-1">
        <div>
          <h3 className="text-[14px] font-bold mb-2 inline-flex items-center gap-1.5"><Filter className="w-4 h-4 text-[#FF0000]" /> {isPt ? "Filtros" : "Filters"}</h3>
          <input type="text" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder={isPt ? "Título/descrição contém..." : "Title/description contains..."} data-testid="yt-filter-q"
            className="w-full al-input h-9 text-[12.5px] mb-2" />
          <input type="text" value={filters.channel} onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
            placeholder={isPt ? "Canal contém..." : "Channel contains..."} data-testid="yt-filter-channel"
            className="w-full al-input h-9 text-[12.5px] mb-2" />
          <div className="grid grid-cols-3 gap-1 text-[10.5px] font-bold uppercase">
            {[{v:null,l:isPt?"Todos":"All"},{v:true,l:isPt?"Com email":"Email"},{v:false,l:"Sem"}].map((o) => (
              <button key={String(o.v)} type="button" onClick={() => setFilters({ ...filters, has_email: o.v })}
                className={`py-1.5 rounded border ${filters.has_email === o.v ? "bg-[#FF0000] text-white border-[#FF0000]" : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#FF0000]"}`}>{o.l}</button>
            ))}
          </div>
        </div>
        <button type="button" onClick={exportCsv} disabled={items.length === 0}
          className="w-full h-9 rounded-lg border border-[var(--al-border)] text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[var(--al-row-alt)] disabled:opacity-50">
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
        {(stats.top_channels || []).length > 0 && (
          <div className="pt-3 border-t border-[var(--al-border)]">
            <h4 className="text-[11px] uppercase font-semibold text-[var(--al-text-muted)] mb-1.5">{isPt ? "Top canais" : "Top channels"}</h4>
            <ul className="space-y-1">
              {(stats.top_channels || []).slice(0, 8).map((o) => (
                <li key={o.channel}>
                  <button type="button" onClick={() => setFilters({ q: "", channel: o.channel, has_email: null })}
                    className="w-full text-left text-[12px] hover:text-[#FF0000] truncate">
                    {o.channel} <span className="text-[var(--al-text-muted)] tabular-nums">· {o.videos}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>

      <div className="lg:col-span-3 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-[15px] font-bold inline-flex items-center gap-2">
            {isPt ? "Resultados" : "Results"}
            <span className="text-[12px] font-medium text-[var(--al-text-muted)] tabular-nums">
              · {items.length} {isPt ? "vídeos" : "videos"}
            </span>
          </h3>
          <KanbanTodayBadge isPt={isPt} />
        </div>
        {items.length === 0 ? (
          <div className="al-card p-10 text-center text-[var(--al-text-muted)]">
            {isPt ? "Nenhum vídeo ainda. Vá em 'Coletar'." : "No videos yet. Go to 'Scrape'."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {items.map((v) => (
              <div key={v.id} className="al-card p-3 flex flex-col gap-1.5">
                {v.thumbnail && (
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={v.thumbnail} alt="" className="w-full h-36 object-cover rounded" loading="lazy" />
                  </a>
                )}
                <a href={v.url} target="_blank" rel="noopener noreferrer"
                   className="text-[13px] font-bold text-[#FF0000] hover:underline line-clamp-2 leading-snug">{v.title}</a>
                <div className="text-[11px] text-[var(--al-text-muted)] truncate inline-flex items-center gap-2">
                  <Tv className="w-3 h-3" /> {v.channel_name || "?"}
                  {v.views && <><span>·</span><Eye className="w-3 h-3" />{v.views}</>}
                </div>
                {v.emails && v.emails.length > 0 && (
                  <div className="text-[11px] text-emerald-600 font-bold inline-flex items-center gap-1 truncate">
                    <Mail className="w-3 h-3" /> {v.emails[0]}
                  </div>
                )}
                <div className="flex gap-1 mt-1 pt-2 border-t border-[var(--al-border)]">
                  <button type="button" onClick={() => promote(v)}
                    className="flex-1 h-7 rounded text-[10.5px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white inline-flex items-center justify-center gap-1">
                    <UserPlus className="w-3 h-3" /> CRM
                  </button>
                  <a href={v.url} target="_blank" rel="noopener noreferrer"
                     className="h-7 px-2 rounded text-[10.5px] font-bold border border-[var(--al-border)] hover:bg-[var(--al-row-alt)] inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RunsTab({ isPt, onIngested }) {
  const [runId, setRunId] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!runId) return;
    setBusy(true);
    try { const { data } = await axios.get(`${API}/youtube/runs/${runId}`); setInfo(data); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const ingest = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/youtube/runs/${runId}/items`, { params: { persist: true, limit: 200 } });
      toast.success(isPt ? `${data.inserted} novos vídeos` : `${data.inserted} new videos`);
      if (onIngested) onIngested();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="al-card p-5 max-w-xl space-y-3" data-testid="yt-runs">
      <h3 className="text-[14px] font-bold inline-flex items-center gap-1.5"><Play className="w-4 h-4 text-[#FF0000]" /> {isPt ? "Inspecionar execução" : "Inspect run"}</h3>
      <input type="text" value={runId} onChange={(e) => setRunId(e.target.value)}
        placeholder="run_id..." className="w-full al-input h-9 text-[12.5px]" />
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={refresh} disabled={busy || !runId}
          className="h-9 px-3 rounded-lg bg-[#FF0000] hover:bg-[#c41d1d] text-white text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Status"}
        </button>
        <button type="button" onClick={ingest} disabled={busy || !runId}
          className="h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
          <Download className="w-3.5 h-3.5" /> {isPt ? "Ingerir" : "Ingest"}
        </button>
      </div>
      {info && (
        <div className="text-[11px] space-y-1 pt-3 border-t border-[var(--al-border)]">
          <div><b>Status:</b> <span className={info.status === "SUCCEEDED" ? "text-emerald-600 font-bold" : "text-amber-600 font-bold"}>{info.status}</span></div>
          <div><b>Started:</b> {info.started_at}</div>
          <div><b>Dataset:</b> <code className="text-[10px]">{info.dataset_id}</code></div>
        </div>
      )}
    </div>
  );
}
